import { useEffect, useEffectEvent, type PropsWithChildren } from "react";
import { useTranslation } from "react-i18next";

import apiClient from "@/api/apiClient";
import queryClient, { MASTER_INVALIDATION_KEY } from "@/api/queryClient";
import { useAuth } from "@/api/resources/auth/hooks";
import { authQueryKeys } from "@/api/resources/auth/queries";
import { useNavigationAnonymous } from "@/hooks/useNavigation";
import { useNotifications } from "@/hooks/useNotifications";
import { clearLocalStorage } from "@/utils/localStorageCache";
import { clearPosthogUser, trackPosthogEvent } from "@/utils/posthog";
import { clearSentryUser } from "@/utils/sentry";

const INVALIDATION_TIMEOUT = 10;

const AuthProvider = ({ children }: PropsWithChildren) => {
  const { t } = useTranslation();
  const notify = useNotifications();
  const { goToLogin } = useNavigationAnonymous();
  const { signOut } = useAuth();

  const performSignOut = useEffectEvent(() => {
    signOut();
  });

  const onSignOut = useEffectEvent(() => {
    // ! Observers torn down during sign-out still run their subscription effect and would
    // ! fetch against the dead session. Disabling queries globally stops those fetches at
    // ! the source; `useSignIn`/`useSignInSSO` re-enable on the next sign-in.
    queryClient.setDefaultOptions({
      queries: { ...queryClient.getDefaultOptions().queries, enabled: false },
    });
    // completely remove all queries in the react-query cache (doesn't trigger re-evaluation)
    queryClient.removeQueries();
    clearLocalStorage();
    trackPosthogEvent(`signOut`, `user`);
    clearSentryUser();
    clearPosthogUser();
    notify.success(t("notify.logout.success"));
    goToLogin();
  });

  useEffect(() => {
    // Timeout "hack", see: https://supabase.com/docs/reference/javascript/auth-onauthstatechange
    let timeout: NodeJS.Timeout;

    const { data } = apiClient.auth.onAuthStateChange((event, newSession) => {
      queryClient.setQueryData([authQueryKeys.getAuthSession], newSession);
      queryClient.setQueryData([authQueryKeys.getAuthUser], newSession?.user);

      if (event !== "INITIAL_SESSION" && event !== "SIGNED_OUT" && !newSession) {
        // if the session is missing, the user is forced to sign out
        performSignOut();
      } else if (event === "INITIAL_SESSION") {
        // re-evaluate ALL app data, if a reload/app visit happens
        timeout = setTimeout(() => {
          void queryClient.invalidateQueries({ queryKey: MASTER_INVALIDATION_KEY });
        }, INVALIDATION_TIMEOUT);
      } else if (event === "SIGNED_IN") {
        // re-evaluate ALL app data, if the auth status has changed
        timeout = setTimeout(() => {
          void queryClient.invalidateQueries({ queryKey: MASTER_INVALIDATION_KEY });
        }, INVALIDATION_TIMEOUT);
      } else if (event === "USER_UPDATED") {
        // re-evaluate all user details if user is updated (session, user, profile, permissions)
        timeout = setTimeout(
          () => void queryClient.invalidateQueries({ queryKey: [...authQueryKeys.all()] }),
          INVALIDATION_TIMEOUT,
        );
      } else if (event === "SIGNED_OUT") {
        onSignOut();
      }
    });
    return () => {
      data.subscription.unsubscribe();
      clearTimeout(timeout);
    };
  }, []);

  return children;
};

export default AuthProvider;
